import { BellRing, Inbox, KeyRound, ShieldCheck, UserRound } from "lucide-react";
import { lazy, Suspense, type ComponentType } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { defineFeature } from "../types";

/**
 * Your account — the notification inbox and the three personal settings screens.
 *
 * The settings pages are contributed through `settingsSections`, which the
 * settings layout renders at `/settings/<path>`; they are still code-split with
 * `React.lazy`, so none of this is in the initial download.
 */

const ProfilePage = lazy(() => import("./settings/profile"));
const SecurityPage = lazy(() => import("./settings/security"));
const NotificationPreferencesPage = lazy(() => import("./settings/notifications"));

function SectionFallback() {
  return (
    <div className="grid w-full gap-5" role="status" aria-label="Loading settings">
      <div className="grid gap-2">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-80" />
      </div>
      <Skeleton className="h-40 w-full" rounded="card" />
      <Skeleton className="h-64 w-full" rounded="card" />
    </div>
  );
}

function section(Page: ComponentType) {
  return (
    <Suspense fallback={<SectionFallback />}>
      <Page />
    </Suspense>
  );
}

export default defineFeature({
  key: "account",
  title: "Your account",
  entities: ["notification", "user"],
  routes: [{ path: "notifications", lazy: () => import("./notifications/page") }],
  settingsSections: [
    {
      key: "profile",
      label: "Profile",
      path: "profile",
      group: "Your account",
      order: 10,
      icon: UserRound,
      description: "Name, photo, timezone and language",
      element: section(ProfilePage),
    },
    {
      key: "security",
      label: "Security",
      path: "security",
      group: "Your account",
      order: 20,
      icon: ShieldCheck,
      description: "Password, two-factor and signed-in devices",
      element: section(SecurityPage),
    },
    {
      key: "notification-preferences",
      label: "Notifications",
      path: "notifications",
      group: "Your account",
      order: 30,
      icon: BellRing,
      description: "What reaches you, on which channel, and when",
      element: section(NotificationPreferencesPage),
    },
  ],
  commands: [
    {
      key: "account.notifications",
      label: "Open notifications",
      icon: Inbox,
      to: "/notifications",
      group: "navigate",
      keywords: ["bell", "alerts", "unread", "inbox"],
      description: "Everything the workspace wanted you to know",
    },
    {
      key: "account.profile",
      label: "Edit your profile",
      icon: UserRound,
      to: "/settings/profile",
      group: "navigate",
      keywords: ["name", "avatar", "photo", "timezone", "language"],
    },
    {
      key: "account.password",
      label: "Change your password",
      icon: KeyRound,
      to: "/settings/security",
      group: "actions",
      keywords: ["security", "2fa", "two factor", "sessions", "sign out"],
    },
    {
      key: "account.notification-preferences",
      label: "Notification preferences",
      icon: BellRing,
      to: "/settings/notifications",
      group: "actions",
      keywords: ["email", "quiet hours", "mute", "digest"],
    },
  ],
});
